import Cookies, { CookieSetOptions } from "universal-cookie";

const DEFAULT_OPTIONS: CookieSetOptions = {
  path: "/",
  secure: import.meta.env.PROD,
  sameSite: "strict" as const,
};

const cookies = new Cookies();

export const SessionAuthCookies = {
  /**
   * Get a session cookie value
   * @returns The cookie value or null if not found
   */
  get: (): string | null | undefined => {
    return cookies.get<string>("session_auth");
  },
  /**
   * Set a session cookie value
   * @param value The cookie value
   * @param options Optional cookie options to override defaults
   */
  set: (value: string, options?: Partial<CookieSetOptions>): void => {
    const finalOptions = { ...DEFAULT_OPTIONS, ...options };
    cookies.set("session_auth", value, finalOptions);
  },
  /**
   * Remove the session cookie
   */
  remove: (): void => {
    cookies.remove("session_auth", { path: "/" });
  },
};

/**
 * Name of the cookie `SidebarProvider` writes on every collapse/expand.
 * Shared so the writer (`components/ui/sidebar.tsx`) and the reader
 * (the protected layout) can never drift apart.
 */
export const SIDEBAR_STATE_COOKIE_NAME = "sidebar_state";

/**
 * Reads the sidebar open flag out of a raw `document.cookie` string.
 * Returns `undefined` when the cookie is absent or holds anything other than
 * `"true"`/`"false"`, so callers keep their own default instead of guessing.
 */
export const parseSidebarStateCookie = (cookieString: string): boolean | undefined => {
  for (const entry of cookieString.split(";")) {
    const separatorIndex = entry.indexOf("=");
    if (separatorIndex === -1) continue;
    if (entry.slice(0, separatorIndex).trim() !== SIDEBAR_STATE_COOKIE_NAME) continue;

    const value = entry.slice(separatorIndex + 1).trim();
    if (value === "true") return true;
    if (value === "false") return false;
    return undefined;
  }

  return undefined;
};

export const SidebarStateCookies = {
  /**
   * Get the persisted sidebar open state.
   * @returns `true`/`false` when the cookie was written, `undefined` otherwise
   */
  get: (): boolean | undefined => {
    if (typeof document === "undefined") return undefined;
    return parseSidebarStateCookie(document.cookie);
  },
};
